import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  NewHelpCenter as HelpCenterLogoIcon,
  Illustrator as IllustratorIcon,
  AvatarDefault,
} from '~components/svgs';
import { Icon } from 'antd';
import { makeStyles } from '@material-ui/styles';
import { useTranslation } from 'react-i18next';
import { Route, Switch } from 'react-router';
import * as accountActions from '~actions/pms-actions/account';
import { Layout } from 'antd';
import { Link } from 'react-router-dom';
import { UserOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { QUERY_ARTICLES } from './queries';
import ArticleDetail from './articleDetail';

const useStyles = makeStyles(theme => ({
  contentWrap: {
    width: 1128,
    margin: '0 auto',
  },
  content: {
    ...theme.flex.row,
    marginTop: 80,
    '& >div:first-child': {
      marginRight: 100,
    },
    '&:first-child': {
      marginTop: 72,
    },
  },
  title: {
    ...theme.typography.h2,
    margin: '24px 0',
    color: theme.colors.titleColor,
    ...theme.flex.crossCenter,
  },
  rectangle: {
    width: 6,
    height: 20,
    background: theme.colors.mainGreen6,
    display: 'inline-block',
    marginRight: 12,
  },
  article: {
    ...theme.typography.body1,
    fontWeight: 600,
    padding: '24px 0',
    color: theme.colors.bodyColor,
    borderBottom: '1px solid #e3e8e8',
    transition: 'all 0.5s',
    '& svg': {
      display: 'none',
      transition: 'all 0.5s',
    },
    '&:hover': {
      ...theme.typography.h4,
      color: theme.colors.mainGreen5,
      padding: 24,
      transition: 'all 0.5s',
      cursor: 'pointer',
      '& svg': {
        transition: 'all 0.5s',
        display: 'block',
      },
    },
  },
  right: {
    width: 480,
    height: 320,
    background: theme.colors.mainGreen1,
  },
  left: { width: 548 },
  articleIcon: {
    color: theme.colors.mainGreen5,
    marginLeft: 8,
  },
  contactBox: {
    ...theme.flex.crossCenter,
    background: theme.colors.gray2,
    marginTop: 72,
    padding: '28px 48px',
  },
  useBox: {
    ...theme.flex.center,
    paddingLeft: 40,
    borderLeft: '1px solid #E3E8E8',
  },
  tip: {
    marginBottom: 8,
    ...theme.typography.h2,
    color: theme.colors.titleColor,
  },
  manageName: {
    ...theme.typography.h5,
    color: theme.colors.titleColor,
  },
  email: {
    ...theme.typography.h5,
    fontWeight: 400,
    color: theme.colors.titleColor,
  },
  dec: {
    ...theme.typography.body1,
    fontWeight: 400,
    color: theme.colors.bodyColor,
  },
}));

const NewHelpCenter = () => {
  const { Header } = Layout;

  const classes = useStyles();

  const dispatch = useDispatch();

  const history = useHistory();

  const { t } = useTranslation();

  const { data: articlesData } = useQuery(QUERY_ARTICLES, {
    variables: {
      status: 'PUBLISHED',
    },
    fetchPolicy: 'no-cache',
  });

  const { cmsUser, token } = useSelector(state => {
    return {
      cmsUser: state.pmsReducer.dashboard.account.toJS().account,
      token: state.auth.getIn(['base', 'token']),
    };
  });

  const { property, booking, payment } = articlesData?.articles || {};

  const articlesArr = [
    {
      type: 'property',
      data: property,
      title: t('cms:new_help_center.property.title'),
    },
    {
      type: 'booking',
      data: booking,
      title: t('cms:new_help_center.booking.title'),
    },
    {
      type: 'payment',
      data: payment,
      title: t('cms:new_help_center.payment.title'),
    },
  ]?.filter(item => !!item?.data?.length);

  const goArticleDetails = articleInfo => {
    const { id } = articleInfo;
    history.push(`/new-help-center/article/${id}`);
  };

  useEffect(() => {
    dispatch(accountActions.getLandlordUser());
  }, [token]);

  const MainContent = () => {
    return (
      <div style={{ background: 'white', paddingBottom: '88px' }}>
        <div className="user-guide__banner">
          <div className="user-guide__banner-container">
            <div className="user-guide__title-container">
              <h1 className="user-guide__title">
                {t('cms:cms.help_center.homepage.title.welcome')}
              </h1>
              <p className="user-guide__summary">
                {t('cms:cms.help_center.homepage.summary.started')}
              </p>
            </div>
            <IllustratorIcon className="user-guide__banner-icon" />
          </div>
        </div>
        <div className={classes.contentWrap}>
          <For each="articleList" of={articlesArr} index="index">
            <If condition={index % 2 === 0}>
              <div className={classes.content}>
                <div className={classes.left}>
                  <div className={classes.title}>
                    <span className={classes.rectangle} />
                    <span> {articleList.title}</span>
                  </div>
                  <div>
                    <If condition={!!articleList?.data?.length}>
                      <For of={articleList?.data?.slice(0, 3)} each="item" index="index">
                        <div
                          className={classes.article}
                          key={`property${index}`}
                          onClick={() => {
                            goArticleDetails(item);
                          }}
                        >
                          {item?.title}
                          <Icon type="arrow-right" className={classes.articleIcon} />
                        </div>
                      </For>
                    </If>
                  </div>
                </div>
                <div className={classes.right} />
              </div>
            </If>
            <If condition={index % 2 !== 0}>
              <div className={classes.content}>
                <div className={classes.right} />
                <div className={classes.left}>
                  <div className={classes.title}>
                    <span className={classes.rectangle} />
                    <span>{articleList.title}</span>
                  </div>
                  <div>
                    <If condition={!!articleList?.data?.length}>
                      <For of={articleList?.data?.slice(0, 3)} each="item" index="index">
                        <div
                          className={classes.article}
                          key={`property${index}`}
                          onClick={() => {
                            goArticleDetails(item);
                          }}
                        >
                          {item?.title}
                          <Icon type="arrow-right" className={classes.articleIcon} />
                        </div>
                      </For>
                    </If>
                  </div>
                </div>
              </div>
            </If>
          </For>
          <div className={classes.contactBox}>
            <div style={{ marginRight: '40px', width: '632px' }}>
              <div className={classes.tip}>{t('cms:new_help_center.contact.tip')}</div>
              <div className={classes.dec}>{t('cms:new_help_center.contact.subtip')}</div>
            </div>

            <div className={classes.useBox}>
              <div>
                <AvatarDefault />
              </div>
              <div
                style={{
                  marginLeft: '16px',
                }}
              >
                <div className={classes.email}>{cmsUser?.company?.accountManager}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  return (
    <Layout className="help-center__container">
      <Header className="help-center__header-container">
        <div className="help-center__header-logo">
          <Link to="/new-help-center">
            <HelpCenterLogoIcon className="help-center__header-logo-icon" />
          </Link>
        </div>
        <div className="help-center__user-info">
          <span className="help-center__icon-container">
            <UserOutlined fill="#ffffff" />
          </span>
          {cmsUser && cmsUser.firstName
            ? t('cms:cms.help_center.header.welcome', {
                firstName: cmsUser.firstName,
              })
            : ''}
          <button className="help-center__logout" onClick={() => {}}>
            {t('cms:cms.help_center.header.log_out')}
          </button>
        </div>
      </Header>
      <div>
        <Switch>
          <Route exact path="/new-help-center/article/:id" component={ArticleDetail} />
          <Route path="/new-help-center" component={MainContent} />
        </Switch>
      </div>
    </Layout>
  );
};

export default NewHelpCenter;
